import React, { useEffect, useState, useRef } from "react";
import Checkbox from '@material-ui/core/Checkbox';
import Select from 'react-select'
import request from "../../helpers/request";
import ReactModal from 'react-modal'
import endpoints from "../../helpers/endpoints";
import measureOptions from "../../helpers/measureOptions.json";
import { completeOrAssuredRequest } from "../../helpers/completeOrAssuredRequest";
import { Stepper, Step, StepLabel, CircularProgress, StepIcon, Typography, Skeleton  } from '@mui/material';

export default function VerifyButton({ summaryData = {}, setSummaryData, userInput = {}, meterUserInput, setUserInput, prefix, setConfirmModal, qualityAssurance, disabled }) {
    const [assuranceOptions, setassuranceOptions] = useState({
        PREMISE: false,
        ADDRESS: false,
        UPRN: false,
        VOA: false,
    })
    const [assuranceReasons, setAssuranceReasons] = useState({
        PREMISE: {},
        ADDRESS: {},
        UPRN: {},
        VOA: {},
    })
    const [assuranceComments, setAssuranceComments] = useState({
        PREMISE: '',
        ADDRESS: '',
        UPRN: '',
        VOA: '',
    })
    const [verificationResult, setVerificationResult] = useState({})
    const [isOpen, setIsOpen] = useState(false)
    const [buttonIsDisabled, setButtonIsDisabled] = useState(true)
    const [completeIsDisabled, setCompleteIsDisabled] = useState(true)
    const [fieldLoading, setFieldLoading] = useState()
    const [confirmIsLoading, setConfirmIsLoading] = useState(false)
    const [verificationFailed, setVerificationFailed] = useState(false)
    const [jobId, setJobId] = useState(null)
    const [jobFetchLoading, setJobFetchLoading] = useState(false)
    const [jobProgressData, setJobProgressData] = useState(null)
    const [dqaStages, setDqaStages] = useState([])
    const pollingIntervalRef = useRef();
    const prevUserInputRef = useRef();
    const jobIdRef = useRef(null);

    const updateJobId = (value) => {
        jobIdRef.current = value
        setJobId(value)
    }

    const updateassuranceOptions = (type) => {
        setassuranceOptions({
            ...assuranceOptions,
            [type]: !assuranceOptions[type]
        })
    }

    const replaceInputValue = (key) => {
        setFieldLoading(key)
        const abpOrVoa = key === "ABP_UPRN" ? "ABP" : "VOA"  
        const identifier = abpOrVoa === "ABP" ? "CF_UPRN" : "CF_VOA" 
        const reasonCode = abpOrVoa === "ABP" ? "CF_UPRN_Reason_Code" : "CF_VOA_Reason_Code"

        setUserInput({
            ...userInput,
            [identifier]: verificationResult[key],
            [reasonCode]: ''
        })
    }

    const reset = () => {
        const keys = Object.keys(assuranceOptions)
        setassuranceOptions(keys.reduce((acc, curr) => (acc[curr] = false, acc), {}))
        setAssuranceReasons(keys.reduce((acc, curr) => (acc[curr] = {}, acc), {}))
        setAssuranceComments(keys.reduce((acc, curr) => (acc[curr] = '', acc), {}))
        setVerificationResult({})
        setIsOpen(false)
        updateJobId(null)
        setJobProgressData(null)
        setDqaStages([])
        prevUserInputRef.current = null
    }

    useEffect(() => {
        if (!isOpen) {
            if (verificationFailed) {
                setVerificationFailed(false)
                setJobProgressData(null)
                updateJobId(null)
                prevUserInputRef.current = null;
            }
            return
        }
        if (JSON.stringify(prevUserInputRef.current) !== JSON.stringify(userInput)) {
            setJobProgressData(null)
            updateJobId(null)
            setDqaStages([])
            prevUserInputRef.current = null;
            if (fieldLoading) {
                setVerificationResult({
                    ...verificationResult,
                    [fieldLoading]: ''
                })
            } else {
                setVerificationResult({})
            }
            const hasCfUprn = !!userInput.CF_UPRN;
            const hasCfUprnReason = !!userInput.CF_UPRN_Reason_Code;
            const hasCmosUprn = !!userInput.CMOS_UPRN;
            const hasCfVoa = !!userInput.CF_VOA;
            const hasCfVoaReason = !!userInput.CF_VOA_Reason_Code;
            const hasClVoa = !!userInput.CMOS_VOA;
            const hasCfAddress = Object.keys(userInput).some(key => key.includes('CF_Address_Line') && userInput[key]);

            const data = {
                Core_SPID: userInput.Core_SPID,
                Customer_Name: userInput.Customer_Name,
                Address_Line_1: hasCfAddress ? userInput.CF_Address_Line_1 : userInput.CMOS_Address_Line_1,
                Address_Line_2: hasCfAddress ? userInput.CF_Address_Line_2 : userInput.CMOS_Address_Line_2,
                Address_Line_3: hasCfAddress ? userInput.CF_Address_Line_3 : userInput.CMOS_Address_Line_3,
                Address_Line_4: hasCfAddress ? userInput.CF_Address_Line_4 : userInput.CMOS_Address_Line_4,
                Address_Line_5: hasCfAddress ? userInput.CF_Address_Line_5 : userInput.CMOS_Address_Line_5,
                Secondary_Addressable_Object: hasCfAddress ? userInput.CF_Secondary_Addressable_Object : userInput.CMOS_Secondary_Addressable_Object,
                Primary_Addressable_Object: hasCfAddress ? userInput.CF_Primary_Addressable_Object : userInput.CMOS_Primary_Addressable_Object,
                Postcode: userInput.CF_Postcode || userInput.CMOS_Postcode
            }

            if (hasCfVoa) {
                data.VOA_BA_Reference = userInput.CF_VOA;
            } else if (hasClVoa && !hasCfVoaReason) {
                data.VOA_BA_Reference = userInput.CMOS_VOA;
            }

            if (!hasCfVoa) {
                if (hasCfVoaReason) {
                    data.CMOS_VOA_Reason_Code = userInput.CF_VOA_Reason_Code;
                } else if (userInput.CMOS_VOA_Reason_Code) {
                    data.CMOS_VOA_Reason_Code = userInput.CMOS_VOA_Reason_Code;
                }
            }

            if (hasCfUprn) {
                data.UPRN = userInput.CF_UPRN;
            } else if (hasCmosUprn && !hasCfUprnReason) {
                data.UPRN = userInput.CMOS_UPRN;
            }

            if (!hasCfUprn) {
                if (hasCfUprnReason) {
                    data.UPRN_Reason_Code = userInput.CF_UPRN_Reason_Code;
                } else if (userInput.CMOS_UPRN_Reason_Code) {
                    data.UPRN_Reason_Code = userInput.CMOS_UPRN_Reason_Code;
                }
            }

            setJobFetchLoading(true)
            request(true).post(endpoints[`${prefix}_VERIFY_ASSURANCE`],
                {
                    spid: summaryData.Core_SPID,
                    data: data
                },
                {
                    doesCancel: true
                }
            ).then(r => {
                updateJobId(r.data.job_id)
                setDqaStages(r.data.stage_names)
                prevUserInputRef.current = userInput;
            }).catch(error => {
                console.log(error)
                window.alert("Failed to verify user input.")
                setVerificationFailed(true)
            }).finally(() => {
                setFieldLoading()
                setJobFetchLoading(false)
            })
        }
    }, [isOpen, userInput, summaryData.Core_SPID, prefix])

    useEffect(() => {
        var isDisabled = false;
        const groups = [
            { reasons: ["CMOS_UPRN_Reason_Code", "CF_UPRN_Reason_Code"], values: ["CMOS_UPRN", "CF_UPRN"] },
            { reasons: ["CMOS_VOA_Reason_Code", "CF_VOA_Reason_Code"], values: ["CMOS_VOA", "CF_VOA"] },
            { values: ["CMOS_Postcode", "CF_Postcode"] }
        ];

        isDisabled = groups.some(group => {
            const reasonsEmpty = group.reasons ? group.reasons.every(reason => !userInput[reason]) : true;
            const valuesEmpty = group.values.every(value => !userInput[value]);
            return reasonsEmpty && valuesEmpty;
        });
        setButtonIsDisabled(isDisabled)
    }, [userInput])

    useEffect(() => {
        setAssuranceReasons(prev => {
            const newReasons = { ...prev };
            Object.keys(assuranceOptions).forEach(option => {
                if (!assuranceOptions[option]) {
                    newReasons[option] = {};
                }
            });
            return newReasons;
        });
    }, [assuranceOptions]);

    useEffect(() => {
        setAssuranceComments(prev => {
            const newComments = { ...prev };
            Object.keys(assuranceReasons).forEach(type => {
                if (!assuranceReasons[type].commentBox) {
                    newComments[type] = "";
                }
            });
            return newComments;
        })
    }, [assuranceReasons])

    useEffect(() => {
        if (disabled || jobFetchLoading || confirmIsLoading) {
            setCompleteIsDisabled(true)
            return
        }
        if ((pollingIntervalRef.current || !jobId) && Object.keys(verificationResult).length === 0) {
            setCompleteIsDisabled(true)
            return
        }
        if (Object.values(assuranceOptions).every(option => option === false)) {
            setCompleteIsDisabled(false)
            return
        }
        Object.keys(assuranceOptions).forEach(option => {
            if (assuranceOptions[option]) {
                setCompleteIsDisabled(!assuranceReasons[option].value)
            }
        })
    }, [assuranceOptions, assuranceReasons, pollingIntervalRef.current, confirmIsLoading])

    useEffect(() => {
        if (jobId) {
            setCompleteIsDisabled(true)
            if (!pollingIntervalRef.current) {
                const pollJobId = jobId
                pollingIntervalRef.current = setInterval(() => {
                    request(true).post(endpoints[`${prefix}_VERIFY_ASSURANCE_UPDATES`],
                        {
                            job_id: pollJobId
                        }
                    ).then(r => {
                        if (jobIdRef.current !== pollJobId) return;
                        if (r.data.status === 'SUCCEEDED') {
                            setCompleteIsDisabled(false);
                            setVerificationResult(r.data.result);
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        setJobProgressData(r.data);
                    }).catch(error => {
                        if (jobIdRef.current !== pollJobId) return;
                        console.log(error)
                        setVerificationFailed(true)
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                        setCompleteIsDisabled(true)
                    })
                }, 1000)
            }
        } else {
            setJobProgressData(null);
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [jobId, prefix]);

    const handleRejectOrAssure = (choice) => {
        setIsOpen(false)
        setConfirmModal(choice)
    }

    const completed = dqaStages.map(stage => {
        if (jobProgressData?.status === 'SUCCEEDED') {
            return true;
        }
        return dqaStages.indexOf(jobProgressData?.stage_progress) > dqaStages.indexOf(stage)
    })

    return [
        <button style={{ width: '100%' }} disabled={buttonIsDisabled} className={`button ${!buttonIsDisabled ? 'background-primary colour-white scale-text' : ''}`} onClick={() => setIsOpen(true)}>Verify Changes</button>,
        <ReactModal
            isOpen={isOpen}
            onRequestClose={() => setIsOpen(false)}
            className="card bulk-allocate"
            contentLabel="Verify Changes"
            style={{ overlay: { backgroundColor: 'rgba(14, 14, 14, 0.55)' }, content: { width: '80em' } }}
        >
            <div className="verify-button-popup">
                {
                    jobFetchLoading ?
                    <>
                        <Skeleton variant="text" height="1em" style={{ justifySelf: 'center'}} >
                            <h4 style={{ textAlign: 'center' }}>Verification Process</h4>
                        </Skeleton>
                        <Skeleton variant="rounded" height="6.5em" />
                        <Skeleton variant="rounded" height="20em" width="100%" />
                    </>
                    :
                    <>
                        <h4 style={{ textAlign: 'center' }}>Verification Process</h4>
                        <div className="verify-button-progress-bar">
                            <Stepper nonLinear activeStep={dqaStages.indexOf(jobProgressData?.stage_progress ?? dqaStages[0])} alternativeLabel >
                                {
                                    dqaStages.map((label, index) => {
                                        const labelProps = {};
                                        const stageFailed = verificationFailed && (jobProgressData?.stage_progress === dqaStages[index] || (!jobProgressData?.stage_progress && index === 0));
                                        if (stageFailed) {
                                            labelProps.optional = (
                                                <Typography variant="caption" color="error">
                                                    Verification process failed. Please contact support.
                                                </Typography>
                                            );
                
                                            labelProps.error = true;
                                        }
                                        return (
                                            <Step key={label} completed={completed[index]} expanded={true}>
                                                <StepLabel
                                                    {...labelProps}
                                                    error={stageFailed}
                                                    slots={{
                                                        ...( !stageFailed && { stepIcon: LoadingStepIcon }),
                                                    }}
                                                >
                                                    {label}
                                                </StepLabel>
                                            </Step>
                                        )
                                    })
                                }
                            </Stepper>
                            <span>{jobProgressData?.progress ?? 0}%</span>
                        </div>
                        <VerifyTable
                            verificationResult={verificationResult}
                            fieldLoading={fieldLoading}
                            assuranceOptions={assuranceOptions}
                            assuranceReasons={assuranceReasons}
                            setAssuranceReasons={setAssuranceReasons}
                            qualityAssurance={qualityAssurance}
                            disabled={disabled}
                            updateassuranceOptions={updateassuranceOptions}
                            pollingIntervalRef={pollingIntervalRef}
                        /> 
                    </>
                }
                {
                    (assuranceReasons.UPRN.commentBox || assuranceReasons.VOA.commentBox) &&
                        <div className="comments">
                            { ['UPRN', 'VOA'].map((type) => {
                                const reducedRows = assuranceReasons.UPRN.commentBox && assuranceReasons.VOA.commentBox
                                return (
                                    assuranceReasons[type].commentBox && 
                                        <div>
                                            <label>{type} Comment:</label>
                                            <textarea onChange={(event) => setAssuranceComments({...assuranceComments, [type]: event.target.value.slice(0, 500)})} value={assuranceComments[type] ?? ''} rows={reducedRows ? 6 : 8} style={{height: '100%', width: '100%', resize: 'none'}}/>  
                                            <div style={{ position: 'relative', bottom: '3px', textAlign: 'end', color: 'gray', fontSize: 12 }}>
                                                {assuranceComments[type].length}/500
                                            </div>
                                        </div>
                                )
                            })}
                        </div>
                }
                <div className="verify-button-options">
                    <button className="button bulk-buy-button compact smaller-text" onClick={() => setIsOpen(false)}>Close</button>
                    {
                        qualityAssurance ?
                            <>
                                <button disabled={disabled} className="button bulk-buy-button compact smaller-text" style={{ gridColumnStart: 3 }} onClick={() => handleRejectOrAssure("Reject")}>Reject</button>
                                <button disabled={disabled} className="button bulk-buy-button compact smaller-text" onClick={() => handleRejectOrAssure("Quality Check Passed")}>Quality Check Passed</button>
                            </>
                        :
                            <>
                                <button className={`button ${verificationResult.ABP_UPRN && verificationResult.ABP_UPRN !== userInput.CF_UPRN?.toString() ? "bulk-buy-button" : ''} compact smaller-text`} disabled={!verificationResult.ABP_UPRN || verificationResult.ABP_UPRN === userInput.CF_UPRN?.toString() || disabled }  onClick={() => replaceInputValue("ABP_UPRN")}>Replace UPRN</button>
                                <button className={`button ${verificationResult.BR_Reference_Number && verificationResult.BR_Reference_Number !== userInput.CF_VOA_BA_Reference?.toString() ? "bulk-buy-button" : ''} compact smaller-text`} disabled={!verificationResult.BR_Reference_Number || verificationResult.BR_Reference_Number === userInput.CF_VOA_BA_Reference?.toString() || disabled} onClick={() => replaceInputValue("BR_Reference_Number")}>Replace VOA</button>
                                <button className={`button ${!completeIsDisabled ? "bulk-buy-button" : ''} compact smaller-text`} disabled={completeIsDisabled || disabled} onClick={() => completeOrAssuredRequest(setConfirmIsLoading, prefix, '', summaryData, Object.values(assuranceOptions).some(option => option === true) ? 'Assured' : 'Complete', userInput, meterUserInput, assuranceReasons, assuranceComments, setSummaryData, reset)} >
                                    {
                                        confirmIsLoading ? 
                                            <i className="fa fa-spinner fa-spin"></i>
                                        :
                                            "Complete"
                                    }
                                </button>
                            </>
                    }
                </div>
            </div>
        </ReactModal>
    ]
}

const VerifyTable = ({ verificationResult, fieldLoading, assuranceOptions, assuranceReasons, setAssuranceReasons, qualityAssurance, disabled, updateassuranceOptions, pollingIntervalRef }) => {
    return (
        <table className="table borders">
            <thead>
                <tr>
                    <th style={{ width: '8em' }}>Element</th>
                    <th style={{ width: '8em' }}>Result</th>
                    <th style={{ width: '10em' }}>Suggestion</th>
                    <th style={{ width: '8em' }}>Reason Code</th>
                    <th style={{ width: '5em' }}>Assure</th>
                    <th style={{ width: '32em' }}>Assure Reason</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Premises</td>
                    <td>{verificationResult.DQA_Premise}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                </tr>
                <tr>
                    <td>Address</td>
                    <td>{verificationResult.DQA_Address}</td>
                    <td></td>
                    <td></td>
                    <td>
                        <Checkbox 
                            key={"address_assured"}
                            color='primary'
                            checked={assuranceOptions['ADDRESS']}
                            onChange={() => updateassuranceOptions('ADDRESS')}
                            disabled={pollingIntervalRef.current || qualityAssurance || disabled}
                        />
                    </td>
                    <td>
                        <Select
                            options={measureOptions.address_options}
                            onChange={(selectedOption) => {setAssuranceReasons({...assuranceReasons, ADDRESS: selectedOption})}}
                            value={assuranceReasons.ADDRESS}
                            placeholder="Select Assurance Reason"
                            isDisabled={!assuranceOptions.ADDRESS || qualityAssurance || disabled}
                        />
                    </td>
                </tr>
                <tr>
                    <td>UPRN</td>
                    <td>
                        {
                            pollingIntervalRef.current && fieldLoading === "ABP_UPRN" ?
                                <i className="fa fa-spinner fa-spin"></i>
                            :
                                verificationResult.DQA_UPRN
                        }
                    </td>
                    <td>{verificationResult.ABP_UPRN}</td>
                    <td>{verificationResult.Client_UPRN_Reason_Code}</td>
                    <td>
                        <Checkbox 
                            key={"uprn_assured"}
                            color='primary'
                            checked={assuranceOptions['UPRN']}
                            onChange={() => updateassuranceOptions('UPRN')}
                            disabled={pollingIntervalRef.current || qualityAssurance || disabled}
                        />
                    </td>
                    <td>
                        <Select
                            options={measureOptions.uprn_options}
                            onChange={(selectedOption) => {setAssuranceReasons({...assuranceReasons, UPRN: selectedOption})}}
                            value={assuranceReasons.UPRN}
                            placeholder="Select Assurance Reason"
                            isDisabled={!assuranceOptions.UPRN || qualityAssurance || disabled}
                        />
                    </td>
                </tr>
                <tr>
                    <td>VOA</td>
                    <td>
                        {
                            pollingIntervalRef.current && fieldLoading === "BR_Reference_Number" ?
                                <i className="fa fa-spinner fa-spin"></i>
                            :
                                verificationResult.DQA_VOA
                        }
                    </td>
                    <td>{verificationResult.BR_Reference_Number}</td>
                    <td>{verificationResult.Client_VOA_Number_Reason_Code}</td>
                    <td>
                        <Checkbox 
                            key={"voa_assured"}
                            color='primary'
                            checked={assuranceOptions['VOA']}
                            onChange={() => updateassuranceOptions('VOA')}
                            disabled={pollingIntervalRef.current || qualityAssurance || disabled}
                        />
                    </td>
                    <td>
                        <Select
                            options={measureOptions.voa_options}
                            onChange={(selectedOption) => {setAssuranceReasons({...assuranceReasons, VOA: selectedOption})}}
                            value={assuranceReasons.VOA}
                            placeholder="Select Assurance Reason"
                            isDisabled={!assuranceOptions.VOA || qualityAssurance || disabled}
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    )
}

function LoadingStepIcon(props) {
    const { active, completed } = props;

    if (active && !completed) {
        return <CircularProgress size={24} />;
    }

    return <StepIcon {...props} />;
}